/**
 * Financial Report Tab Component
 *
 * Component for displaying marketer financial report
 */

import React, {useEffect} from 'react';
import {Marketer} from '../models/report.model';
import {useReports} from '../hooks/useReports';
import {InlineLoadingState} from '../../../Presentation/components/loading/InlineLoadingState';
import {FinancialSummaryCard} from './FinancialSummaryCard';
import {TopCustomersTable} from './TopCustomersTable';
import {AppCardTitle} from '../../../Presentation/components/ui/Typography';
import Card from '../../../Presentation/components/ui/Card';
import {useTableExport, useExportConfig} from '../../../Presentation/hooks/useTableExport';
import ExportButton from '../../../Presentation/components/ui/ExportButton';
import {ExportFormatters} from '../../../Presentation/utils/exportFormatters';

interface FinancialReportTabProps {
  marketer: Marketer;
}

export const FinancialReportTab: React.FC<FinancialReportTabProps> = ({marketer}) => {
  const {
    financialReport,
    isLoadingFinancialReport,
    error,
    getMarketerFinancialReport,
    filters
  } = useReports();

  // Export configuration for top customers - moved to top to avoid conditional hook calls
  const {createConfig} = useExportConfig<any>();
  const customersExportConfig = createConfig([
    {key: 'customer_name', title: 'اسم العميل'},
    {key: 'total_amount', title: 'إجمالي المبلغ', formatter: ExportFormatters.currencySARExport},
    {key: 'paid_amount', title: 'المدفوع', formatter: ExportFormatters.currencySARExport},
    {
      key: 'remaining',
      title: 'المتبقي',
      formatter: (_, row: any) => ExportFormatters.currencySARExport((row.total_amount - row.paid_amount).toString())
    },
    {
      key: 'payment_rate',
      title: 'معدل الدفع (%)',
      formatter: (_, row: any) => {
        const paymentRate = row.total_amount > 0
          ? (row.paid_amount / row.total_amount) * 100
          : 0;
        return `${paymentRate.toFixed(1)}%`;
      }
    }
  ], 'أفضل العملاء', 'تصدير بيانات أفضل العملاء', 'تصدير قائمة أفضل العملاء مع تفاصيل المدفوعات');

  // Export hook - moved to top to avoid conditional hook calls
  const customersExport = useTableExport({
    data: financialReport?.top_customers || [],
    config: customersExportConfig
  });

  useEffect(() => {
    getMarketerFinancialReport(marketer.id, filters);
  }, [marketer.id, filters, getMarketerFinancialReport]);

  if (isLoadingFinancialReport) {
    return <InlineLoadingState message="جاري تحميل التقرير المالي..." />;
  }

  // Error is handled by StandardizedErrorHandler and shown as toast notification
  // No need to display inline error - just show empty state if no data
  if (!financialReport) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">لا توجد بيانات متاحة</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Financial Summary */}
      <FinancialSummaryCard
        summary={financialReport.financial_summary}
        showAdditionalMetrics={true}
      />

      {/* Top Customers */}
      <Card>
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <AppCardTitle className="text-gray-900">أفضل العملاء</AppCardTitle>
            <ExportButton
              onExport={customersExport.exportData}
              isExporting={customersExport.isExporting}
              canExport={customersExport.canExport}
              recordCount={customersExport.recordCount}
              showDropdown={true}
            />
          </div>
        </div>
        <TopCustomersTable customers={financialReport.top_customers} />
      </Card>
    </div>
  );
};
